import { useCallback, useEffect, useRef, useState } from "react";
import { loadCoachSessionSettings } from "../utils/liveTrainingSettings";
import {
  OFF_TABLE_CHRONO_SOURCE,
  getStationCameraTarget,
  normalizeStationCameraTarget,
  setStationCameraTarget,
  stationCameraRecordingIdentity,
  stationCameraTargetKey,
} from "../utils/stationCameraBinding";

const UPLOAD_WAIT_MS = 45000;

function dedupeRecordingTargets(targets) {
  const seen = new Set();
  const out = [];
  for (const target of targets || []) {
    const identity = stationCameraRecordingIdentity(target);
    if (!identity) continue;
    const key =
      identity.kind === "table"
        ? `table:${identity.tableNumber}`
        : `user:${identity.userId}`;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(identity);
  }
  return out;
}

function payloadForTarget(base, target) {
  const payload = { ...base };
  if (target.tsgId) payload.tsg_id = target.tsgId;
  if (target.kind === "user") {
    payload.receiver_user_id = target.userId;
    if (target.playerId) payload.player_id = target.playerId;
    payload.table_number = target.tableNumber || 0;
  } else if (target.kind === "table") {
    payload.table_number = target.tableNumber;
  }
  return payload;
}

let sessionSeq = 0;
function newRecordingSessionId() {
  sessionSeq += 1;
  return `chrono-${Date.now().toString(36)}-${sessionSeq}`;
}

/**
 * Coach-side off-table chrono video: start/stop a remote session camera phone
 * (player or table/admin phone). The managing coach device never opens getUserMedia.
 *
 * Each Record → Stop is its own recording session. A new session can start
 * while the previous clip is still uploading; uploads are matched back to
 * their session by capture id.
 */
export function useChronoSessionVideo({
  teId,
  tsId = null,
  coachUserId = null,
  socket = null,
  stationKey = null,
  /** When true, Record starts every camera phone in the session (timer-only blocks). */
  captureAllCameras = false,
}) {
  const [recording, setRecording] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [error, setError] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);
  const [messageId, setMessageId] = useState(null);
  const [epochAt, setEpochAt] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [cameraTarget, setCameraTargetState] = useState(() =>
    getStationCameraTarget(stationKey)
  );
  /**
   * Live recording session:
   * { id, epochMs, epochIso, targets, captureAll, captureIds: Set }
   */
  const activeRef = useRef(null);
  /** Start acks still expected, oldest session first: Array<{ session, remaining, ok }>. */
  const startAcksRef = useRef([]);
  const stopAcksRef = useRef(0);
  /** Sessions waiting for their clip: Array<{ session, resolve, timer }>. */
  const uploadWaitsRef = useRef([]);
  const selectionRef = useRef({
    targets: [],
    captureAll: false,
    explicit: false,
  });

  useEffect(() => {
    setCameraTargetState(getStationCameraTarget(stationKey));
  }, [stationKey]);

  const setCameraTarget = useCallback(
    (next, { bindToStation = false } = {}) => {
      const normalized = normalizeStationCameraTarget(next);
      setCameraTargetState(normalized);
      if (bindToStation && stationKey) {
        setStationCameraTarget(stationKey, normalized);
      }
    },
    [stationKey]
  );

  const bindCurrentDeviceToStation = useCallback(() => {
    if (!stationKey) return;
    setStationCameraTarget(stationKey, cameraTarget);
  }, [stationKey, cameraTarget]);

  const setRecordingSelection = useCallback((next) => {
    selectionRef.current = {
      targets: dedupeRecordingTargets(next?.targets),
      captureAll: Boolean(next?.captureAll),
      explicit: true,
    };
  }, []);

  const markOffsetMs = useCallback(() => {
    const active = activeRef.current;
    if (!active || active.epochMs == null) return null;
    return Math.max(0, Date.now() - active.epochMs);
  }, []);

  const removeUploadWait = useCallback((wait) => {
    if (!wait) return;
    if (wait.timer) window.clearTimeout(wait.timer);
    uploadWaitsRef.current = uploadWaitsRef.current.filter((w) => w !== wait);
    setUploadCount(uploadWaitsRef.current.length);
  }, []);

  useEffect(() => {
    if (!socket) return undefined;

    const onAck = (ack = {}) => {
      const action = String(ack.action || "");
      if (action === "start_video") {
        const pending = startAcksRef.current[0];
        if (!pending) return;
        const { session } = pending;
        const isActive = activeRef.current === session;
        const failed =
          ack.ok === false ||
          (ack.broadcast !== true &&
            ack.delivered != null &&
            Number(ack.delivered) === 0);
        if (!failed) {
          pending.ok += 1;
          if (ack.capture_id) session.captureIds.add(String(ack.capture_id));
          if (pending.ok === 1 && isActive) {
            const startedAt =
              ack.started_at ||
              new Date(ack.media_sync_at || Date.now()).toISOString();
            const epochMs =
              Number(ack.media_sync_at) > 0
                ? Number(ack.media_sync_at)
                : Date.parse(startedAt) || session.epochMs;
            session.epochMs = epochMs;
            session.epochIso = startedAt;
            setEpochAt(startedAt);
          }
        }
        pending.remaining -= 1;
        if (pending.remaining > 0) return;
        startAcksRef.current = startAcksRef.current.slice(1);
        if (pending.ok === 0) {
          if (isActive) {
            activeRef.current = null;
            setRecording(false);
            setSessionId(null);
          }
          const wait = uploadWaitsRef.current.find(
            (w) => w.session === session
          );
          if (wait) {
            removeUploadWait(wait);
            wait.resolve({
              url: null,
              messageId: null,
              videoEpochAt: session.epochIso,
              sessionId: session.id,
              failed: true,
            });
          }
          setError(
            ack.ok === false
              ? ack.code === "missing_fields"
                ? "Pick a player or table camera phone first"
                : ack.code || "Could not start station camera"
              : "No camera phone online. Open Live Training on a player or table phone."
          );
          return;
        }
        setError(null);
        return;
      }
      if (action === "stop_video") {
        if (stopAcksRef.current <= 0) return;
        stopAcksRef.current -= 1;
        if (ack.ok === false) {
          setError(ack.code || "Could not stop station camera");
        }
      }
    };

    const onUploaded = (payload = {}) => {
      if (!teId || String(payload.te_id || "") !== String(teId)) return;
      const url = payload.video_message_url || null;
      const mid = payload.message_id || null;
      if (!url && !mid) return;
      const waits = uploadWaitsRef.current;
      if (!waits.length) return;
      const captureId =
        payload.capture_id != null ? String(payload.capture_id) : "";
      // Older backends omit capture_id: first waiting session wins.
      const wait = captureId
        ? waits.find((w) => w.session.captureIds.has(captureId)) ||
          waits.find((w) => w.session.captureIds.size === 0)
        : waits[0];
      if (!wait) return;
      removeUploadWait(wait);
      setVideoUrl(url);
      setMessageId(mid);
      setError(null);
      wait.resolve({
        url,
        messageId: mid,
        videoEpochAt: wait.session.epochIso,
        sessionId: wait.session.id,
      });
    };

    socket.on("coachTableRecordingAck", onAck);
    socket.on("evaluationVideoUploaded", onUploaded);
    return () => {
      socket.off("coachTableRecordingAck", onAck);
      socket.off("evaluationVideoUploaded", onUploaded);
    };
  }, [socket, teId, removeUploadWait]);

  const startRecording = useCallback(async () => {
    const current = activeRef.current;
    if (current) {
      return { epochAt: current.epochIso, sessionId: current.id };
    }
    setError(null);

    if (!socket || !teId || !coachUserId) {
      setError("Not connected to the session");
      throw new Error("Socket required");
    }

    const fallback = stationCameraRecordingIdentity(
      normalizeStationCameraTarget(cameraTarget) ||
        getStationCameraTarget(stationKey)
    );
    const selected = selectionRef.current;
    const useAll = captureAllCameras || selected.captureAll;
    const targets = useAll
      ? []
      : selected.explicit
        ? selected.targets
        : selected.targets.length
          ? selected.targets
          : fallback
            ? [fallback]
            : [];
    if (!useAll && !targets.length) {
      setError("Pick a station, player, or table camera phone");
      throw new Error("Station camera required");
    }

    // Optimistic epoch so Start/Stop offsets work while the ack arrives.
    const now = Date.now();
    const session = {
      id: newRecordingSessionId(),
      epochMs: now,
      epochIso: new Date(now).toISOString(),
      targets,
      captureAll: useAll,
      captureIds: new Set(),
    };
    activeRef.current = session;
    startAcksRef.current = [
      ...startAcksRef.current,
      { session, remaining: useAll ? 1 : targets.length, ok: 0 },
    ];

    const fps = loadCoachSessionSettings(tsId).recommendedRecordingFps;
    const base = {
      te_id: teId,
      ts_id: tsId,
      coach_id: coachUserId,
      source: OFF_TABLE_CHRONO_SOURCE,
      fps,
    };
    if (useAll) {
      socket.emit("start_table_video_recording", {
        ...base,
        capture_all: true,
        table_number: 0,
      });
    } else {
      for (const target of targets) {
        socket.emit(
          "start_table_video_recording",
          payloadForTarget(base, target)
        );
      }
    }

    setSessionId(session.id);
    setEpochAt(session.epochIso);
    setRecording(true);
    setElapsedSec(0);
    return { epochAt: session.epochIso, sessionId: session.id };
  }, [
    cameraTarget,
    stationKey,
    socket,
    teId,
    tsId,
    coachUserId,
    captureAllCameras,
  ]);

  const emitStop = useCallback(
    (session, extra = {}) => {
      if (!socket || !session) return;
      const base = {
        te_id: teId,
        ts_id: tsId,
        coach_id: coachUserId,
        source: OFF_TABLE_CHRONO_SOURCE,
        ...extra,
      };
      const targets = session.captureAll ? [] : session.targets || [];
      if (!targets.length) {
        stopAcksRef.current += 1;
        socket.emit("stop_table_video_recording", {
          ...base,
          capture_all: true,
          table_number: 0,
        });
        return;
      }
      for (const target of targets) {
        stopAcksRef.current += 1;
        socket.emit(
          "stop_table_video_recording",
          payloadForTarget(base, target)
        );
      }
    },
    [socket, teId, tsId, coachUserId]
  );

  const stopAndUpload = useCallback(async () => {
    const session = activeRef.current;
    if (!socket || !teId || !coachUserId || !session) return null;

    activeRef.current = null;
    setError(null);
    setRecording(false);
    setSessionId(null);

    const uploaded = new Promise((resolve) => {
      const wait = { session, resolve, timer: null };
      wait.timer = window.setTimeout(() => {
        removeUploadWait(wait);
        resolve({
          url: null,
          messageId: null,
          videoEpochAt: session.epochIso,
          sessionId: session.id,
          timedOut: true,
        });
      }, UPLOAD_WAIT_MS);
      uploadWaitsRef.current = [...uploadWaitsRef.current, wait];
      setUploadCount(uploadWaitsRef.current.length);
    });
    emitStop(session);
    return uploaded;
  }, [socket, teId, coachUserId, emitStop, removeUploadWait]);

  /** Drop the live session; camera phones stop without uploading. */
  const discard = useCallback(() => {
    const session = activeRef.current;
    activeRef.current = null;
    if (session) emitStop(session, { discard: true });
    setRecording(false);
    setSessionId(null);
    setEpochAt(null);
  }, [emitStop]);

  useEffect(() => {
    if (!recording) return undefined;
    const id = setInterval(() => {
      const active = activeRef.current;
      if (active?.epochMs != null) {
        setElapsedSec((Date.now() - active.epochMs) / 1000);
      }
    }, 250);
    return () => clearInterval(id);
  }, [recording]);

  const discardRef = useRef(discard);
  discardRef.current = discard;
  useEffect(
    () => () => {
      discardRef.current();
      for (const wait of uploadWaitsRef.current) {
        if (wait.timer) window.clearTimeout(wait.timer);
      }
      uploadWaitsRef.current = [];
    },
    []
  );

  return {
    recording,
    uploading: uploadCount > 0,
    error,
    elapsedSec,
    videoUrl,
    messageId,
    epochAt,
    sessionId,
    cameraTarget,
    deviceId: stationCameraTargetKey(cameraTarget) || null,
    devices: [],
    setCameraTarget,
    setRecordingSelection,
    setDeviceId: (id, opts) => {
      // Back-compat for older callers expecting deviceId strings.
      if (!id) {
        setCameraTarget(null, opts);
        return;
      }
      const raw = String(id);
      if (raw.startsWith("station:")) {
        const parts = raw.slice(8).split(":");
        const tsgId = parts[0];
        const userId = parts.slice(1).join(":");
        if (tsgId && userId) {
          setCameraTarget({ kind: "station", tsgId, userId }, opts);
        }
        return;
      }
      if (raw.startsWith("user:")) {
        setCameraTarget({ kind: "user", userId: raw.slice(5) }, opts);
      } else if (raw.startsWith("table:")) {
        setCameraTarget(
          { kind: "table", tableNumber: Number(raw.slice(6)) },
          opts
        );
      }
    },
    bindCurrentDeviceToStation,
    refreshDevices: () => {},
    startRecording,
    stopAndUpload,
    markOffsetMs,
    discard,
  };
}
